// A tiny element builder and a few formatters. No framework.

export type Child = Node | string | number | null | undefined | false | Child[];
type Props = Record<string, unknown> | null | undefined;

function append(el: Node, kids: Child[]): void {
  for (const k of kids) {
    if (k == null || k === false) continue;
    if (Array.isArray(k)) append(el, k);
    else el.appendChild(typeof k === "object" ? k : document.createTextNode(String(k)));
  }
}

function apply(el: Element, props: Props): void {
  if (!props) return;
  for (const [key, v] of Object.entries(props)) {
    if (v == null || v === false) continue;
    if (key === "class") el.setAttribute("class", String(v));
    else if (key === "style") el.setAttribute("style", String(v));
    else if (key.startsWith("on") && typeof v === "function") el.addEventListener(key.slice(2).toLowerCase(), v as EventListener);
    else if (key === "dataset") Object.assign((el as HTMLElement).dataset, v);
    else if (key in el && key !== "list" && !(el instanceof SVGElement)) (el as unknown as Record<string, unknown>)[key] = v;
    else el.setAttribute(key, v === true ? "" : String(v));
  }
}

export function h<K extends keyof HTMLElementTagNameMap>(tag: K, props?: Props, ...kids: Child[]): HTMLElementTagNameMap[K];
export function h(tag: string, props?: Props, ...kids: Child[]): HTMLElement;
export function h(tag: string, props?: Props, ...kids: Child[]): HTMLElement {
  const el = document.createElement(tag);
  apply(el, props);
  append(el, kids);
  return el;
}

export function svg(tag: string, props?: Props, ...kids: Child[]): SVGElement {
  const el = document.createElementNS("http://www.w3.org/2000/svg", tag);
  apply(el, props);
  append(el, kids);
  return el;
}

export function clear(el: Element): void {
  while (el.firstChild) el.removeChild(el.firstChild);
}

export function mount(el: Element, ...kids: Child[]): void {
  clear(el);
  append(el, kids);
}

const MON = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
/** "SEP 14" (or "SEP 14 2024" when withYear). */
export function fmtDay(iso: string | null | undefined, withYear = false): string {
  if (!iso || iso.length < 10) return "";
  const m = MON[+iso.slice(5, 7) - 1] ?? "";
  return `${m} ${+iso.slice(8, 10)}${withYear ? " " + iso.slice(0, 4) : ""}`;
}
export const fmtMD = (iso: string): string => (iso && iso.length >= 10 ? iso.slice(5, 10) : "");
export const num = (n: number): string => n.toLocaleString("en-US");

/** Debounce for search inputs. */
export function debounce<A extends unknown[]>(fn: (...a: A) => void, ms: number): (...a: A) => void {
  let t: ReturnType<typeof setTimeout> | undefined;
  return (...a: A) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...a), ms);
  };
}

export const tick = (): Promise<void> => new Promise((r) => requestAnimationFrame(() => setTimeout(r, 0)));

export function typing(t: EventTarget | null): boolean {
  const e = t as HTMLElement | null;
  if (!e || !e.tagName) return false;
  return e.tagName === "INPUT" || e.tagName === "TEXTAREA" || e.tagName === "SELECT" || e.isContentEditable;
}
